import { inject, Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LineOfBusiness } from 'src/app/shared/models/enums/line-of-business';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { ComplianceFacilitiesService } from '../../facilities/facilities.service';
import { ClientPortalVisibilitySettings } from '../../models/client-portal-visibility-settings.model';
import { ClientPortalVisibilityUpdate } from '../../models/client-portal-visibility-update.model';
import { ComplianceFacilityBulkEdit } from '../../models/compliance-facility-bulk-edit';
import { ComplianceFacility } from '../../models/compliance-facility.model';
import { DocDueDate } from '../../models/doc-due-date.model';
import {
    FacilityAutoMatchSettingsDto as LegacyFacilityAutoMatchSettingsDto,
    FacilityAutoMatchSettingsViewModel as LegacyFacilityAutoMatchSettingsViewModel
} from '../../models/facility-auto-match-settings-dto.model';
import { FacilityDocDueDates } from '../../models/facility-doc-due-dates.model';
import { ProfileRequirement } from '../../models/profile-requirements.model';
import { CategoryLineOfBusinessEnum } from '../enums/category-line-of-business-enum.enum';
import { LineOfBusinessEnum } from '../enums/line-of-business-enum.enum';
import { FacilitiesClient } from '../facilities-client.service';
import { ClientPortalVisibilitySettingsDto } from '../models/client-portal-visibility-settings-dto.model';
import { ClientPortalVisibilityUpdateDto } from '../models/client-portal-visibility-update-dto.model';
import { ComplianceFacilityBulkEditDto } from '../models/compliance-facility-bulk-edit-dto.model';
import { ComplianceFacilityDto } from '../models/compliance-facility-dto.model';
import { DocDueDateDto } from '../models/doc-due-date-dto.model';
import { FacilityAutoMatchSettingsDto } from '../models/facility-auto-match-settings-dto.model';
import { FacilityAutoMatchSettingsViewModel } from '../models/facility-auto-match-settings-view-model.model';
import { FacilityDocDueDatesDto } from '../models/facility-doc-due-dates-dto.model';
import { FacilityFilterDto } from '../models/facility-filter-dto.model';
import { KeyValueDto } from '../models/key-value-dto.model';
import { PagingTokenOfComplianceFacilityDto } from '../models/paging-token-of-compliance-facility-dto.model';
import { ProfileRequirementsDto } from '../models/profile-requirements-dto.model';
import { ProfileRequirementsFilterDto } from '../models/profile-requirements-filter-dto.model';
import { NSwagMigrationService } from './nswag-migration.service';
type GetFacilityRequirementsParams = Parameters<typeof FacilitiesClient.prototype.getFacilityRequirements>;
// 13 endpoints
@Injectable({ providedIn: 'root' })
export class ComplianceFacilitiesNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'ComplianceFacilitiesNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationComplianceFacilitiesService;
    private readonly legacy = inject(ComplianceFacilitiesService);
    private readonly facilitiesClient = inject(FacilitiesClient);

    // ** FacilitiesController.GetFacilities
    getFacilities(args: FetchArgs): Observable<PagingToken<ComplianceFacility[]> | PagingTokenOfComplianceFacilityDto> {
        const filter: FacilityFilterDto = args.filter ?? {};

        return this.invoke((x) =>
            x
                ? this.legacy.getFacilities(args)
                : this.facilitiesClient.getFacilities(
                      filter.id, // filter_Id
                      filter.name, // filter_Name
                      filter.completeStatus, // filter_CompleteStatus
                      filter.lineOfBusiness, // filter_LineOfBusiness
                      filter.liaisonName, // filter_LiaisonName
                      filter.accountManager, //filter_AccountManager
                      filter.qmAssociationId, // filter_QmAssociationId
                      filter.qmAssociation, // filter_QmAssociation
                      filter.hospitalSystem, // filter_HospitalSystem
                      filter.contractGroup, // filter_ContractGroup
                      filter.updatedDate, // filter_UpdatedDate
                      filter.credentialingSpecialistId, // filter_CredentialingSpecialistId
                      filter.stateId, // filter_StateId
                      filter.brandId, // filter_BrandId
                      filter.vmsMsp, // filter_VmsMsp
                      filter.software, // filter_Software
                      filter.contractGroupId, // filter_ContractGroupId
                      filter.autoMatchStrategy, // filter_AutoMatchStrategy
                      args.pageSize, // pageSize
                      args.skip, // skip
                      args.sort?.field, // sort_Field
                      args.sort?.dir as any, // sort_Type
                      undefined // paginated
                  )
        );
    }

    // ** FacilitiesController.GetComplianceFacility
    getFacility(id: number): Observable<ComplianceFacility | ComplianceFacilityDto> {
        return this.invoke((x) => (x ? this.legacy.getFacility(id) : this.facilitiesClient.getComplianceFacility(id)));
    }

    // ** FacilitiesController.GetFacilityRequirements
    getFacilityRequirements(
        id: number,
        lob: LineOfBusiness | LineOfBusinessEnum,
        args: FetchArgs
    ): Observable<ProfileRequirement[] | ProfileRequirementsDto[]> {
        const { legacy, featureManager, facilitiesClient } = this;
        const filter: ProfileRequirementsFilterDto = args.filter ?? {};

        return this.invoke((x) => {
            if (x) {
                return legacy.getFacilityRequirements(id, lob as LineOfBusiness, args);
            }

            // Normalize
            const normalizedLob =
                lob === LineOfBusinessEnum.Locums || lob === LineOfBusinessEnum.AccessControl
                    ? lob
                    : LineOfBusinessEnum.Travel;
             const clob = this.convertToClob(normalizedLob) as any; // replace FLOB with CLOB

            // Create Parameters
            const params: GetFacilityRequirementsParams = [
                id, // id
                clob, // lob
                filter.requirementId, // filter_RequirementId
                filter.requirementName, // filter_RequirementName
                filter.taskType, // filter_TaskType
                filter.clientPortalVisibility, //filter_ClientPortalVisibility
                filter.requirementLevel, // filter_RequirementLevel
                filter.qualificationSettingsLevel, // filter_QualificationSettingsLevel
                filter.linesOfBusiness, // filter_LinesOfBusiness
                filter.isDefault, // filter_IsDefault
                filter.expirationRequired, // filter_ExpirationRequired
                filter.requirementDescription, // filter_RequirementDescription
                filter.externalRequirementDescription, // filter_ExternalRequirementDescription
                filter.categoryLineOfBusinessId, // filter_CategoryLineOfBusinessId
                filter.acceptanceCriteriaType, // filter_AcceptanceCriteriaType
                filter.acceptanceCriteriaStatus, // filter_AcceptanceCriteriaStatus
                args.pageSize, //  pageSize
                args.skip, // skip
                args.sort?.field, // sort_Field
                args.sort?.dir as any, // sort_Type
                undefined // paginated
            ];     
            return facilitiesClient.getFacilityRequirements(
                ...(params as unknown as GetFacilityRequirementsParams)
            );      
        });
    }

    // ** FacilitiesController.GetClientPortalNetworkVisibility
    getClientPortalVisibility(
        id: number
    ): Observable<ClientPortalVisibilitySettings | ClientPortalVisibilitySettingsDto> {
        return this.invoke((x) =>
            x ? this.legacy.getClientPortalVisibility(id) : this.facilitiesClient.getClientPortalNetworkVisibility(id)
        );
    }

    // ** FacilitiesController.UpdateRequirementClientPortalVisibility
    updateClientPortalVisibility(
        id: number,
        updates: ClientPortalVisibilityUpdate[] | ClientPortalVisibilityUpdateDto[]
    ): Observable<unknown | void> {
        return this.invoke((x) =>
            x
                ? this.legacy.updateClientPortalVisibility(id, updates)
                : this.facilitiesClient.updateRequirementClientPortalVisibility(id.toString(), updates)
        );
    }

    // ** FacilitiesController.CopyRequirements
    copyRequirements(copyFrom: number, copyTo: number[]): Observable<unknown | void> {
        return this.invoke((x) =>
            x
                ? this.legacy.copyRequirements(copyFrom, copyTo)
                : this.facilitiesClient.copyRequirements(copyFrom, copyTo)
        );
    }

    // ** FacilitiesController.SearchFacilities
    searchFacilities(name: string): Observable<KeyValue<number, string>[] | KeyValueDto[]> {
        return this.invoke((x) =>
            x ? this.legacy.searchFacilities(name) : this.facilitiesClient.searchFacilities(name)
        );
    }

    // ** FacilitiesController.GetDocDueDate
    getDocDueDate(facilityId: number): Observable<FacilityDocDueDates | FacilityDocDueDatesDto> {
        return this.invoke((x) =>
            x ? this.legacy.getDocDueDate(facilityId) : this.facilitiesClient.getDocDueDate(facilityId)
        );
    }

    // ** FacilitiesController.UpdateDocDueDate
    updateDocDueDate(facilityId: number, docDueDate: DocDueDate | DocDueDateDto): Observable<unknown | void> {
        return this.invoke((x) =>
            x
                ? this.legacy.updateDocDueDate(facilityId, docDueDate)
                : this.facilitiesClient.updateDocDueDate(facilityId, docDueDate)
        );
    }

    // ** FacilitiesController.UpdateCompleteStatus
    updateFacilityCompleteStatus(facility: ComplianceFacility | ComplianceFacilityDto): Observable<unknown | void> {
        return this.invoke((x) =>
            x
                ? this.legacy.updateFacilityCompleteStatus(facility as ComplianceFacility)
                : this.facilitiesClient.updateCompleteStatus(facility.id, facility as ComplianceFacilityDto)
        );
    }

    // ** FacilitiesController.UpdateBulkCompleteStatus
    updateBulkFacilityCompleteStatus(
        bulkEdit: ComplianceFacilityBulkEdit | ComplianceFacilityBulkEditDto
    ): Observable<unknown | void> {
        return this.invoke((x) =>
            x
                ? this.legacy.updateBulkFacilityCompleteStatus(bulkEdit)
                : this.facilitiesClient.updateBulkCompleteStatus(bulkEdit)
        );
    }

    // ** FacilitiesController.GetAutoMatchSettingsAsync
    getAutoMatchSettings(
        facilityId: number
    ): Observable<LegacyFacilityAutoMatchSettingsViewModel | FacilityAutoMatchSettingsViewModel> {
        return this.invoke((x) =>
            x ? this.legacy.getAutoMatchSettings(facilityId) : this.facilitiesClient.getAutoMatchSettings(facilityId)
        );
    }

    // ** FacilitiesController.UpdateAutoMatchSettingsAsync
    updateAutoMatchSettings(settings: LegacyFacilityAutoMatchSettingsDto | FacilityAutoMatchSettingsDto): Observable<void> {
        return this.invoke((x) => x
            ? this.legacy.updateAutoMatchSettings(settings as LegacyFacilityAutoMatchSettingsDto)
            : this.facilitiesClient.updateAutoMatchSettings(settings as FacilityAutoMatchSettingsDto));
    }

    // ** FacilitiesController.CheckLabRequestConfigurationExistsForFacilitiesAsync
    checkLabRequestConfigurationExistsForFacilities(facilityIds: number[]): Observable<boolean> {
        return this.invoke((x) =>
            x
                ? this.legacy.checkLabRequestConfigurationExistsForFacilities(facilityIds)
                : this.facilitiesClient.checkLabRequestConfigurationExistsForFacilities(facilityIds)
        );
    }

    private convertToClob(lob: LineOfBusinessEnum): CategoryLineOfBusinessEnum {
        switch (lob) {
            case LineOfBusinessEnum.Travel:
                return CategoryLineOfBusinessEnum.Travel;
            case LineOfBusinessEnum.Locums:
                return CategoryLineOfBusinessEnum.Locums;
            case LineOfBusinessEnum.AccessControl:
                return CategoryLineOfBusinessEnum.AccessControl;
            default:
                return null;
        }
    }
}
